import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo.jsx';
import { Button } from '../components/ui.jsx';


/**
 * The two side-specific landing pages.
 *
 * /for-creators leads with UGC because that is the search intent and the money
 * — "UGC deals" is what creators actually type. /for-brands leads with finding
 * creators whose numbers can be checked.
 */

export function ForCreators() {
  return (
    <>
      <Seo
        title="Get UGC deals and brand partnerships"
        description={`Publish your rates, get found by brands, and see public programs' full terms before you apply. Free to join.`}
      />

      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div className="max-w-3xl">
            <p className="text-2xs font-semibold uppercase tracking-widest text-accent">
              For creators
            </p>
            <h1 className="mt-3 text-5xl font-semibold tracking-tight sm:text-6xl">
              Get UGC deals and brand partnerships.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ink-muted">
              Publish your rates once. Brands find you, and you can read a program’s full terms
              before you spend a minute on an application.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/start/creator" size="lg">
                Get started free
              </Button>
              <Button to="/programs" size="lg" variant="outline">
                Browse programs first
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-rule bg-paper-sunken">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">
            You stop negotiating in the dark
          </h2>
          <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3">
            {[
              {
                title: 'See the rate before you apply',
                body: 'Every program publishes its commission, cookie window and payout schedule. You can compare five brands in a minute instead of five email threads.',
              },
              {
                title: 'Your price is on your profile',
                body: '“DM for pricing” costs you the deals where your rate was fine and the brand never asked. Publishing it filters for you, all day.',
              },
              {
                title: 'The terms can’t change under you',
                body: 'When a brand accepts you, the rate is frozen to your partnership. If they cut it next week, your commissions keep paying the rate you agreed.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-paper-raised p-5">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">
                Your numbers, straight from the platform
              </h2>
              <p className="mt-3 text-ink-muted">
                Connect TikTok, Instagram, YouTube or a Facebook Page and your followers, average
                views and engagement come from the platform itself, marked verified. Affily never
                posts. One connected account is all you need to apply.
              </p>
              <p className="mt-3 text-ink-muted">
                That is deliberately in your favour. Brands can trust a verified number at a glance,
                and anything you add by hand — a blog, a newsletter — is labelled as yours, right
                next to the number.
              </p>
              <p className="mt-3 text-ink-muted">
                Anything Affily measured itself — clicks, conversions, completed partnerships — is
                shown separately and marked as tracked.
              </p>
            </div>

            <div className="rounded-lg border border-rule bg-paper-raised p-5">
              <h3 className="text-sm font-semibold">The transparency score</h3>
              <p className="mt-1.5 text-sm text-ink-muted">
                A number out of 100 for how much checkable information your profile carries. The
                whole rubric is published, every criterion has a stated weight, and your editor
                lists exactly what you’re missing and what it’s worth.
              </p>
              <p className="mt-3 text-sm text-ink-muted">
                It measures disclosure, not performance, and there is no way to buy it.
              </p>
              <Link
                to="/transparency"
                className="mt-3 inline-block text-sm font-medium text-accent hover:underline"
              >
                Read the rubric →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-rule bg-paper-sunken">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">Free tools you can use now</h2>
          <p className="mt-2 text-ink-muted">No account needed for either.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/media-kit-generator"
              className="rounded-lg border border-rule bg-paper-raised p-5 transition-colors hover:border-accent"
            >
              <h3 className="font-semibold">Media kit generator</h3>
              <p className="mt-1.5 text-sm text-ink-muted">
                Fill in a form, get a clean media kit, download it as a PDF and send it today.
              </p>
              <span className="mt-3 inline-block text-sm font-medium text-accent">Open →</span>
            </Link>
            <Link
              to="/tools/engagement-rate-calculator"
              className="rounded-lg border border-rule bg-paper-raised p-5 transition-colors hover:border-accent"
            >
              <h3 className="font-semibold">Engagement rate calculator</h3>
              <p className="mt-1.5 text-sm text-ink-muted">
                Work out your real rate and see where it sits for your audience size.
              </p>
              <span className="mt-3 inline-block text-sm font-medium text-accent">Open →</span>
            </Link>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">
            Free to join. A small fee when you get paid.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-muted">
            No listing fee, no subscription. Affily only charges on commissions it actually tracked
            and paid out — your rate is in your account, and on every commission line before it
            reaches you.
          </p>
          <Button to="/start/creator" size="lg" className="mt-6">
            Create your free profile
          </Button>
        </div>
      </section>
    </>
  );
}

export function ForBrands() {
  return (
    <>
      <Seo
        title="Find UGC creators and influencers with transparent, verified stats"
        description="List a partnership program with public terms and find creators whose audience numbers you can check. Free to list. Tracking, attribution and payouts included."
      />

      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div className="max-w-3xl">
            <p className="text-2xs font-semibold uppercase tracking-widest text-accent">
              For brands
            </p>
            <h1 className="mt-3 text-5xl font-semibold tracking-tight sm:text-6xl">
              Find UGC creators and influencers with transparent, verified stats.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ink-muted">
              Publish your terms once and stop running the same negotiation forty times. Creators
              self-select on a rate they can already see.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/start/brand" size="lg">
                List a program free
              </Button>
              <Button to="/creators" size="lg" variant="outline">
                Browse creators first
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-rule bg-paper-sunken">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">Why publishing your rate works</h2>
          <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3">
            {[
              {
                title: 'Better applications, fewer of them',
                body: 'Creators who apply have already read your commission, your cookie window and your content rules. The ones your rate doesn’t suit never write to you.',
              },
              {
                title: 'Audience numbers you can check',
                body: 'Self-reported stats are labelled as such and every listed platform links out to a public profile, so you can verify a claim in ten seconds instead of trusting a screenshot.',
              },
              {
                title: 'Tracking that holds up',
                body: 'Deduplicated clicks, last-click attribution inside your cookie window, and a signed server-to-server postback. Conversions we couldn’t attribute are shown to you, not hidden.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-paper-raised p-5">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Two lines to integrate</h2>
              <p className="mt-3 text-ink-muted">
                Drop the pixel on your order confirmation page, or fire a signed postback from your
                server. Both are idempotent on your own order id, so installing both cannot pay a
                commission twice.
              </p>
              <p className="mt-3 text-ink-muted">
                Your program page gives you the snippet and your signing secret.
              </p>
            </div>
            <pre className="overflow-x-auto rounded-lg border border-rule bg-ink p-4 text-xs leading-relaxed text-paper">
              <code>{`<script src="https://affily.me/api/track/pixel.js"></script>
<script>
  affily.conversion({
    program: 'YOUR_PROGRAM_ID',
    orderId: order.id,
    amountCents: order.totalCents
  });
</script>`}</code>
            </pre>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">
            Free to list. You pay creators for results.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-muted">
            No listing fee and no subscription. Affily charges a small fee only on commissions it
            tracked and paid out — your rate is shown in your account before you pay anything.
          </p>
          <Button to="/start/brand" size="lg" className="mt-6">
            List a program free
          </Button>
        </div>
      </section>
    </>
  );
}
